/**
 * Shared domain layer for the orbit: the item shape and the pure read-outs
 * both halves of the orbit need — the SVG scene in `OrbitStage` and the
 * always-rendered list in `NodeRail`.
 *
 * There is no `OrbitView` component any more. The design places the scene and
 * the goal list in two different regions of the page (`Dashboard`'s
 * instrument grid), not two columns of one widget, so `Dashboard` composes
 * `OrbitStage` and `NodeRail` directly. What used to live here as one
 * component's internals is now the small set of facts both of them read the
 * same way — so "on track" and "3 days left" can never drift between the
 * scene and the list because two components duplicated the arithmetic.
 *
 * The orbit now holds every node kind, not just metrics — a family tree has
 * no unit, no precision, and can never carry a goal.
 *
 * `OrbitItem` is RE-EXPORTED from `api.ts` rather than redeclared here. It
 * used to be a second copy, "kept structurally identical", which is a promise
 * no type system enforces: adding `startDate` to the goal in `api.ts` left
 * this copy behind, the two shapes stopped matching, and TypeScript quietly
 * chose the non-narrowing overload of `Array.filter` instead of reporting it.
 * The error then surfaced in `Dashboard`, in a `.map` nobody had touched,
 * complaining that `unit` might be null. Two hours of the wrong file.
 */

import type { OrbitItem } from '../api.js';

export type { OrbitItem };

export type GoalInfo = NonNullable<OrbitItem['goal']>;
export type GoalStatus = 'on-track' | 'achieved' | 'overdue';

/** An `OrbitItem` known to be a metric with a real unit and precision — the
 *  only shape `formatTarget` (and anything else that reads a target) is
 *  allowed to accept, so a non-metric item can never reach a `.toFixed` call
 *  on a `null` precision. */
export type MetricOrbitItem = OrbitItem & { kind: 'metric'; unit: string; precision: number };

export function isMetricOrbitItem(item: OrbitItem): item is MetricOrbitItem {
  return item.kind === 'metric' && item.unit !== null && item.precision !== null;
}

export function goalStatus(goal: GoalInfo): GoalStatus {
  if (goal.progress.achieved) return 'achieved';
  if (goal.progress.daysRemaining !== null && goal.progress.daysRemaining < 0) return 'overdue';
  return 'on-track';
}

export function fractionOf(goal: GoalInfo): number {
  return goal.progress.fraction ?? 0;
}

/** How many entries read out as, for a node with no series worth charting —
 *  "3 entries", "1 entry", "Nothing yet". Shared by `formatValue` (a
 *  non-metric node) and `ariaLabelFor`. */
function entryCountReadOut(item: OrbitItem): string {
  if (item.entryCount === 0) return 'Nothing yet';
  return item.entryCount === 1 ? '1 entry' : `${item.entryCount} entries`;
}

export function formatValue(item: OrbitItem): string {
  if (!isMetricOrbitItem(item)) return entryCountReadOut(item);
  if (item.latestValue === null) return 'No readings yet';
  return `${item.latestValue.toFixed(item.precision)} ${item.unit}`;
}

/** Metric-only by construction: `MetricOrbitItem` is the only shape this
 *  accepts, so a caller cannot pass a family tree's `null` unit/precision in
 *  even by mistake — the compiler refuses it. */
export function formatTarget(item: MetricOrbitItem, goal: GoalInfo): string {
  return `${goal.targetValue.toFixed(item.precision)} ${item.unit}`;
}

export function daysLabel(daysRemaining: number | null): string | null {
  if (daysRemaining === null || daysRemaining <= 0) return null;
  return daysRemaining === 1 ? '1 day left' : `${daysRemaining} days left`;
}

export function ariaLabelFor(item: OrbitItem): string {
  if (!isMetricOrbitItem(item)) {
    return `${item.title}, ${entryCountReadOut(item).toLowerCase()}`;
  }

  const value = item.latestValue === null ? 'no readings yet' : formatValue(item);
  if (!item.goal) {
    return `${item.title}, watched, latest reading ${value}`;
  }
  const { progress } = item.goal;
  const target = formatTarget(item, item.goal);
  const progressPart = progress.achieved
    ? 'goal achieved'
    : progress.fraction === null
      ? `progress not yet known, target ${target}`
      : `${Math.round(progress.fraction * 100)}% toward target of ${target}`;
  return `${item.title}, latest reading ${value}, ${progressPart}`;
}
